import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, FileText, X } from 'lucide-react';
import {
  getFullListingDescription,
  getListingDetailsTitle,
  getListingLinks,
} from '../../utils/listingDetails';
import './ListingDetailsModal.css';

const focusableSelector =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * One full-screen description/link experience for every listing-card dialect.
 * It is mounted once by FlippableListingCard, so homepage, share-link, internal,
 * and external cards all use the same dismissal and outbound-link behavior.
 */
export default function ListingDetailsModal({
  listing = {},
  open,
  onClose,
  subtitle = null,
  actions = null,
}) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const panel = panelRef.current;
    panel?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !panel) return;
      const focusable = [...panel.querySelectorAll(focusableSelector)];
      if (!focusable.length) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [onClose, open]);

  if (!open || typeof document === 'undefined') return null;

  const description = getFullListingDescription(listing);
  const links = getListingLinks(listing);
  const roleTitle = getListingDetailsTitle(listing);

  return createPortal(
    <div
      className="listing-details-modal__backdrop"
      onMouseDown={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <section
        ref={panelRef}
        className="listing-details-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="listing-details-title"
        aria-describedby="listing-details-description"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="listing-details-modal__header">
          <div className="listing-details-modal__heading">
            <FileText size={20} aria-hidden="true" />
            <div>
              <p className="listing-details-modal__eyebrow">Complete posting</p>
              <h2 id="listing-details-title">{roleTitle}</h2>
              {subtitle && (
                <p className="listing-details-modal__subtitle">{subtitle}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            className="listing-details-modal__close"
            onClick={onClose}
            aria-label="Close listing details"
          >
            <X size={22} aria-hidden="true" />
          </button>
        </header>

        <div className="listing-details-modal__body">
          <p id="listing-details-description">{description}</p>
        </div>

        {links.length > 0 && (
          <footer className="listing-details-modal__links" aria-label="Listing links">
            {links.map((link) => (
              <a
                key={link.key}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="listing-details-modal__link"
              >
                <span>{link.label}</span>
                <ExternalLink size={17} aria-hidden="true" />
              </a>
            ))}
          </footer>
        )}

        {actions && (
          <footer className="listing-details-modal__actions">{actions}</footer>
        )}
      </section>
    </div>,
    document.body,
  );
}
